import type { RioTrip, RioTripsFilters } from "@types";
import { mergeSelectedTrip, mergeTripSources, toTripOptions, TripDriverOption } from "@utils/rioTripOptions";
import { useTpgTrips } from "@components/shared/useTpgTrips";
import { SelectOption } from "@rio-cloud/rio-uikit";
import {
  getActiveBranches,
  getActiveCategories,
  getDrivers,
  getActiveSegments,
  getActiveSubCategories,
  getActiveSuppliers,
  getRioTrips,
} from "@services";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FormState } from "./form";
import { useGetStoredAssets, useGetStoredSelectedVehicles } from "@store/tmsAssetTree/tmsAssetTreeSlice";
import { useLocalizedName } from "@hooks";
import { getDriverName } from "@utils/driverName";

export interface InactiveSelectOption extends SelectOption {
  inactive?: boolean;
}

const initialDefaultForm = {
  date: "",
  value: "",
  branch: "",
  segment: "",
  supplier: "",
  trip: "",
  vehicle: "",
  driver: [],
  category: "",
  subcategory: "",
  observation: "",
  taxes: "",
};

export const useFormReceipt = () => {
  const [branchs, setBranchs] = useState<InactiveSelectOption[]>([]);
  const [segments, setSegments] = useState<InactiveSelectOption[]>([]);
  const [supplier, setSupplier] = useState<InactiveSelectOption[]>([]);
  const [drivers, setDrivers] = useState<(SelectOption & TripDriverOption)[]>([]);
  const [categories, setCategories] = useState<InactiveSelectOption[]>([]);
  const [subCategories, setSubCategories] = useState<InactiveSelectOption[]>([]);
  const [vehicles, setVehicles] = useState<SelectOption[]>([]);
  const [tripsData, setTripsData] = useState<RioTrip[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<RioTrip | null>(null);
  const assets = useGetStoredAssets();
  const localizedName = useLocalizedName();
  const { locale } = useIntl();

  const [form, setForm] = useState<FormState>(initialDefaultForm);
  const vehiclesSelected = useGetStoredSelectedVehicles();

  const mountOption = (data: any[]): InactiveSelectOption[] => {
    return data?.map((res: { id: number; name: string; pt?: string; en?: string; es?: string; active?: boolean }) => ({
      id: res.id.toString(),
      label: localizedName(res),
      inactive: res.active === false,
    }));
  };

  const fetchBranchs = async () => {
    try {
      const response = await getActiveBranches();
      setBranchs(mountOption(response));
    } catch {
      setBranchs([]);
    }
  };

  const fetchSegments = async () => {
    try {
      const response = await getActiveSegments();
      setSegments(mountOption(response));
    } catch {
      setSegments([]);
    }
  };

  const fetchSupplier = async () => {
    try {
      const response = await getActiveSuppliers("CLIENTE");
      setSupplier(mountOption(response));
    } catch {
      setSupplier([]);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await getDrivers();
      setDrivers(
        response.map((driver: Record<string, unknown>) => {
          const rioDriverId = driver.rioDriverId as string | undefined;
          return {
            id: String(driver.id as string | number),
            label: getDriverName(driver),
            rioDriverId: rioDriverId != null ? String(rioDriverId) : undefined,
          };
        })
      );
    } catch {
      setDrivers([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getActiveCategories();
      const filtered = response.filter((res) => res.type === "RECEITA");

      setCategories(mountOption(filtered));
    } catch {
      setCategories([]);
    }
  };

  const fetchSubCategories = async () => {
    try {
      const response = await getActiveSubCategories(form.category);
      setSubCategories(mountOption(response.data));
    } catch {
      setSubCategories([]);
    }
  };

  const fetchVehicles = async () => {
    if (!assets) {
      return;
    }

    try {
      const filtered =
        vehiclesSelected.length > 0 ? assets?.filter((item) => vehiclesSelected?.includes(item.id)) : assets;
      setVehicles(mountOption(filtered));
    } catch {
      setSubCategories([]);
    }
  };

  const { tpgTrips, addTpgTrip } = useTpgTrips();

  const availableTrips = useMemo(
    () => mergeSelectedTrip(mergeTripSources(tripsData, tpgTrips), selectedTrip),
    [tripsData, tpgTrips, selectedTrip]
  );

  const trips = useMemo<SelectOption[]>(() => toTripOptions(availableTrips), [availableTrips]);

  const fetchTrips = async (filters?: RioTripsFilters) => {
    try {
      const page = await getRioTrips(filters);
      setTripsData(page.content);
    } catch {
      setTripsData([]);
    }
  };

  useEffect(() => {
    fetchBranchs();
    fetchSegments();
    fetchSupplier();
    fetchDrivers();
    fetchCategories();
    fetchVehicles();
    // Refaz a busca quando o idioma muda para reaplicar os nomes localizados
    // (pt/en/es) vindos do backend, que ficavam presos no idioma da 1ª carga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    fetchVehicles();
  }, [assets, vehiclesSelected]);

  useEffect(() => {
    if (form?.category) {
      fetchSubCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.category]);

  return {
    branchs,
    segments,
    supplier,
    drivers,
    categories,
    subCategories,
    vehicles,
    trips,
    tripsData: availableTrips,
    selectedTrip,
    setSelectedTrip,
    tpgTrips,
    addTpgTrip,
    fetchVehicles,
    fetchTrips,
    fetchSubCategories,
    fetchBranchs,
    fetchSegments,
    fetchSupplier,
    fetchDrivers,
    fetchCategories,
    form,
    setForm,
    initialDefaultForm,
  };
};
